/* assets/jintcm-article.js — Blog article page enhancements (spec §5.2–5.4).
   Progressive: the page is complete without this file. No dependencies, no network calls. */
(function () {
  'use strict';
  var MIN_H2_FOR_TOC = 3;

  function slugify(text) {
    var s = String(text || '').toLowerCase().trim()
      .replace(/[^a-z0-9一-鿿]+/g, '-').replace(/^-+|-+$/g, '');
    return s || 'section';
  }
  function uniqueId(base, used) {
    var id = base, n = 2;
    while (used[id]) { id = base + '-' + n; n += 1; }
    used[id] = true;
    return id;
  }
  function shouldBuildToc(count) { return count >= MIN_H2_FOR_TOC; }
  // True when str has any character other than whitespace (\s covers NBSP and BOM; ​ is zero-width space).
  function hasText(str) { return /[^\s​]/.test(String(str || '')); }
  function scrollPercent(scrollTop, scrollHeight, clientHeight) {
    var max = scrollHeight - clientHeight;
    if (max <= 0) return 0;
    return Math.min(100, Math.max(0, scrollTop / max * 100));
  }
  function activeIndex(tops, offset) {
    var cur = 0, i;
    for (i = 0; i < tops.length; i += 1) { if (tops[i] < offset) cur = i; }
    return cur;
  }

  var api = { slugify: slugify, uniqueId: uniqueId, shouldBuildToc: shouldBuildToc, scrollPercent: scrollPercent, activeIndex: activeIndex, hasText: hasText };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document === 'undefined') return;

  // Read at click time, so a preference changed after load is respected.
  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Pasted fonts/colours must not override the design system.
  function cleanProse(prose) {
    var styled = prose.querySelectorAll('[style]'), i;
    for (i = 0; i < styled.length; i += 1) styled[i].removeAttribute('style');
    var fonts = prose.querySelectorAll('font');
    for (i = 0; i < fonts.length; i += 1) {
      var f = fonts[i];
      while (f.firstChild) f.parentNode.insertBefore(f.firstChild, f);
      f.parentNode.removeChild(f);
    }
  }

  function isImageOnly(el) {
    return el.tagName === 'P' && !!el.querySelector('img') && el.textContent.replace(/\s+/g, '') === '';
  }
  function isItalicOnly(el) {
    if (el.tagName !== 'P') return false;
    var text = el.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return false;
    var it = el.querySelectorAll('em, i'), got = '', i;
    for (i = 0; i < it.length; i += 1) got += it[i].textContent;
    return got.replace(/\s+/g, ' ').trim() === text;
  }
  // An italic-only paragraph right after an image-only paragraph is that image's caption.
  function tagCaptions(prose) {
    var kids = prose.children, i;
    for (i = 0; i < kids.length - 1; i += 1) {
      if (isImageOnly(kids[i]) && isItalicOnly(kids[i + 1])) {
        kids[i].classList.add('jtcm-art__figure');
        kids[i + 1].classList.add('jtcm-art__caption');
      }
    }
  }
  // In a blockquote with two or more paragraphs, the last one is the attribution.
  function tagAttributions(prose) {
    var qs = prose.querySelectorAll('blockquote'), i;
    for (i = 0; i < qs.length; i += 1) {
      var ps = qs[i].querySelectorAll(':scope > p');
      if (ps.length >= 2) ps[ps.length - 1].classList.add('jtcm-art__attrib');
    }
  }

  function pad2(n) { return n < 10 ? '0' + n : String(n); }

  // A shared #slug link lands on its section once the ids exist (the browser ran before they were generated).
  function scrollToHash(hs) {
    var id = '';
    try { id = decodeURIComponent(location.hash.replace(/^#/, '')); } catch (err) { return; }
    if (!id) return;
    var i;
    for (i = 0; i < hs.length; i += 1) {
      if (hs[i].id === id) { hs[i].scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'auto', block: 'start' }); return; }
    }
  }

  // Returns a function that refreshes the highlighted item, or null when no rail is built.
  function buildToc(root, prose) {
    // Empty rich-text headings (e.g. <h2><br></h2>, <h2>&nbsp;</h2>) neither count nor appear in the rail.
    var hs = Array.prototype.filter.call(prose.querySelectorAll('h2'), function (h) { return hasText(h.textContent); });
    if (!shouldBuildToc(hs.length)) return null;
    var rail = root.querySelector('[data-art-rail]'), list = root.querySelector('[data-art-toc]'), grid = root.querySelector('[data-art-grid]');
    if (!rail || !list || !grid) return null;
    var used = {}, links = [], i;
    var existing = prose.querySelectorAll('[id]');
    for (i = 0; i < existing.length; i += 1) used[existing[i].id] = true;
    Array.prototype.forEach.call(hs, function (h, idx) {
      if (!h.id) h.id = uniqueId(slugify(h.textContent), used);
      h.setAttribute('tabindex', '-1');
      var li = document.createElement('li'), a = document.createElement('a'), num = document.createElement('span');
      num.className = 'jtcm-art__toc-num'; num.textContent = pad2(idx + 1);
      a.className = 'jtcm-art__toc-link'; a.href = '#' + h.id;
      a.appendChild(num); a.appendChild(document.createTextNode(h.textContent.trim()));
      a.addEventListener('click', function (e) {
        e.preventDefault();
        h.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'start' });
        if (history.replaceState) history.replaceState(null, '', '#' + h.id);
        h.focus({ preventScroll: true });
      });
      li.appendChild(a); list.appendChild(li); links.push(a);
    });
    rail.hidden = false;
    grid.setAttribute('data-toc', 'on');
    scrollToHash(hs);
    var offset = (parseFloat(getComputedStyle(hs[0]).scrollMarginTop) || 0) + 1;
    return function refresh() {
      var tops = Array.prototype.map.call(hs, function (h) { return h.getBoundingClientRect().top; });
      var cur = activeIndex(tops, offset);
      links.forEach(function (a, k) { if (k === cur) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current'); });
    };
  }

  function watchScroll(root, refreshToc) {
    var bar = root.querySelector('[data-art-progress]'), ticking = false;
    function update() {
      var d = document.documentElement;
      if (bar) bar.style.width = scrollPercent(d.scrollTop || window.pageYOffset, d.scrollHeight, d.clientHeight) + '%';
      if (refreshToc) refreshToc();
      ticking = false;
    }
    window.addEventListener('scroll', function () { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }, { passive: true });
    update();
  }

  function legacyCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      var ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (e) { return false; }
  }
  function wireCopy(root) {
    root.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-copy-link]') : null;
      if (!btn) return;
      var box = btn.closest('[data-share]'), status = box && box.querySelector('[data-copy-status]');
      function done(ok) {
        if (!status) return;
        status.textContent = btn.getAttribute(ok ? 'data-copied' : 'data-failed') || '';
        if (ok) btn.classList.add('is-copied');
        window.setTimeout(function () { status.textContent = ''; btn.classList.remove('is-copied'); }, 2500);
      }
      var url = btn.getAttribute('data-url') || window.location.href;
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(function () { done(true); }, function () { done(legacyCopy(url)); });
      else done(legacyCopy(url));
    });
  }

  // "Back" returns to the previous page when it was on this site, otherwise follows the link (the blog index).
  function wireBack(root) {
    var back = root.querySelector('[data-art-back]');
    if (!back) return;
    back.addEventListener('click', function (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      try {
        if (document.referrer && new URL(document.referrer).origin === window.location.origin && window.history.length > 1) { e.preventDefault(); window.history.back(); }
      } catch (err) { /* follow the link */ }
    });
  }

  function init() {
    var root = document.querySelector('[data-art]');
    if (!root) return;
    var prose = root.querySelector('[data-art-prose]'), refresh = null;
    if (prose) { cleanProse(prose); tagCaptions(prose); tagAttributions(prose); refresh = buildToc(root, prose); }
    watchScroll(root, refresh);
    wireCopy(root);
    wireBack(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
